/**
 * ProfileList - Image processing profiles
 *
 * Features:
 * - List of available profiles
 * - Upload new .dstyle files
 * - Set default profile
 * - Preview profile settings
 */

import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchProfiles,
  uploadProfile,
  deleteProfile,
  setDefaultProfile,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './ProfileList.module.css';

function UploadModal({ onUpload, onClose }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-fill name from filename
      if (!name) {
        setName(selectedFile.name.replace('.dstyle', ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !name.trim()) return;
    setUploading(true);
    setError(null);
    try {
      await onUpload(file, name.trim());
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        Upload Profile
      </Modal.Header>
      <Modal.Body>
        <p className={styles.uploadDescription}>
          Upload a style file (.dstyle) to use as a processing profile.
        </p>

        {error && <div className={styles.formError}>{error}</div>}

        <div className={styles.formGroup}>
          <label className={styles.label}>Profile Name</label>
          <input
            type="text"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Sports Vibrant"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Style File</label>
          <div className={styles.fileInput}>
            <input
              type="file"
              accept=".dstyle"
              onChange={handleFileChange}
              id="profile-file"
              className={styles.hiddenInput}
            />
            <label htmlFor="profile-file" className={styles.fileLabel}>
              {file ? file.name : 'Choose .dstyle file...'}
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={uploading || !file || !name.trim()}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function ProfileList() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Confirmation/alert modal states (replaces browser confirm/alert)
  const [confirmDeleteProfile, setConfirmDeleteProfile] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProfiles({ limit: 100 });
      setProfiles(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleUpload = async (file, name) => {
    await uploadProfile(file, name);
    loadProfiles();
  };

  const handleDelete = (profile) => {
    if (profile.is_default) {
      setAlertMessage('Cannot delete the default profile. Set another profile as default first.');
      return;
    }
    setConfirmDeleteProfile(profile);
  };

  const confirmDeleteProfileAction = async () => {
    if (!confirmDeleteProfile) return;
    const profile = confirmDeleteProfile;
    setConfirmDeleteProfile(null);
    try {
      await deleteProfile(profile.id);
      loadProfiles();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleSetDefault = async (profile) => {
    try {
      await setDefaultProfile(profile.id);
      loadProfiles();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (loading && profiles.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading profiles...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Profiles</h2>
          <p className={styles.subtitle}>
            Image processing profiles for photo output
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowUploadModal(true)}>
          + Upload Profile
        </Button>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className={styles.profileGrid}>
        {profiles.map((profile) => (
          <div
            key={profile.id}
            className={clsx(styles.profileCard, profile.is_default && styles.default)}
          >
            <div className={styles.profileIcon}>P</div>
            <div className={styles.profileInfo}>
              <h3 className={styles.profileName}>
                {profile.name}
                {profile.is_default && (
                  <span className={styles.defaultBadge}>Default</span>
                )}
              </h3>
              <p className={styles.profileMeta}>
                {profile.source_display || 'Unknown source'}
              </p>
              {profile.created_at && (
                <p className={styles.profileDate}>
                  Added {new Date(profile.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className={styles.profileActions}>
              {!profile.is_default && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSetDefault(profile)}
                >
                  Set Default
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDelete(profile)}
                disabled={profile.is_default}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}

        {profiles.length === 0 && (
          <div className={styles.empty}>
            No profiles found. Upload a .dstyle file to get started.
          </div>
        )}
      </div>

      {showUploadModal && (
        <UploadModal
          onUpload={handleUpload}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {/* Alert Modal (replaces browser alert) */}
      <Modal
        isOpen={!!alertMessage}
        onClose={() => setAlertMessage(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setAlertMessage(null)}>
          Notice
        </Modal.Header>
        <Modal.Body>
          <p>{alertMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setAlertMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        isOpen={!!confirmDeleteProfile}
        onClose={() => setConfirmDeleteProfile(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setConfirmDeleteProfile(null)}>
          Confirm Delete
        </Modal.Header>
        <Modal.Body>
          <p>Delete profile &ldquo;{confirmDeleteProfile?.name}&rdquo;?</p>
          <p style={{ marginTop: 8, fontSize: 13, color: '#999' }}>
            This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setConfirmDeleteProfile(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteProfileAction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
